/**
 * Keyboard Manager
 * Handles global keyboard shortcuts for the application.
 * Extracted from main.js for better separation of concerns.
 */

import * as undoManager from "../../../engine/core/undo-manager.js";
import { showToast } from "../ui/toast.js";
import { t } from "../../../shared/i18n/index.js";

// Track mouse position for cursor-based shortcuts
let lastMouseX = 0;
let lastMouseY = 0;

// References for cleanup
let mouseMoveHandler = null;
let keydownHandler = null;

// Stored callbacks
let callbacks = null;

// Flag to temporarily disable shortcuts (e.g., when markdown view is active)
let shortcutsEnabled = true;

/**
 * Get the node ID under the current mouse cursor position.
 * Works for mindmap, bubble, and gantt views.
 * @param {string} currentView - The current active view
 * @returns {string|null} - Node ID or null if no node under cursor
 */
function getNodeUnderCursor(currentView, { listRows = false } = {}) {
  const elementsUnderMouse = document.elementsFromPoint(lastMouseX, lastMouseY);

  if (currentView === "mindmap") {
    for (const el of elementsUnderMouse) {
      if (el.classList.contains("mm-node") && el.dataset.id) {
        return el.dataset.id;
      }
    }
  }
  if (currentView === "bubble") {
    for (const el of elementsUnderMouse) {
      if (el.classList.contains("bubble") && el.dataset.id) {
        return el.dataset.id;
      }
    }
  }
  if (currentView === "gantt") {
    // A timeline bar, or (for T and W only) a row of the task list on the
    // left: rows are dense, so cut / copy / paste / edit keep to bars.
    for (const el of elementsUnderMouse) {
      if ((el.classList.contains("gantt-bar") || (listRows && el.classList.contains("gantt-label-row"))) && el.dataset.id) {
        return el.dataset.id;
      }
    }
  }
  return null;
}

/**
 * Setup keyboard shortcuts for the app.
 *
 * @param {Object} cbs - Callbacks object with dependencies:
 *   - isActive: () => boolean (false while another interface is shown)
 *   - isModalOpen: () => boolean
 *   - getState: () => AppState
 *   - isReadOnly: () => boolean
 *   - isDeleteBlocked: () => boolean (read-only, and deleting too; a full map allows it)
 *   - isMapFull: () => boolean
 *   - findNode: (id) => Node
 *   - findParent: (id) => Node
 *   - setFocus: (id) => void
 *   - setSelected: (id) => void
 *   - updateNode: (id, updates) => void
 *   - addNode: (parentId, node) => void
 *   - removeNode: (id) => void
 *   - switchView: (viewName) => void
 *   - toggleRoutinesPanel: () => void
 *   - openModal: (id) => void
 *   - showShortcutsModal: () => void
 *   - showToast: (message, options) => void
 *   - toggleNotesOverlay: () => void
 *   - toggleNodeRefnote: (nodeId) => void
 *   - openTagPopup: (nodeId, mouseX, mouseY) => void
 *   - makeEditable: (labelEl, nodeId, viewType, mouseX, mouseY) => void
 *   - generateId: () => string
 *   - getNewColor: (existingColors, excludeColor) => string
 *   - getViewModule: (viewName) => ViewModule
 *   - screenToWorld: (x, y, parentId) => {x, y}
 *   - setBubblePosition: (docId, nodeId, x, y) => void
 *   - getCurrentDocId: () => string
 *   - copyNodes: (nodeId) => void
 *   - cutNodes: (nodeId) => void
 *   - pasteNodes: (parentId) => void
 */
export function setupKeyboardShortcuts(cbs) {
  callbacks = cbs;

  // Track mouse position
  mouseMoveHandler = (e) => {
    lastMouseX = e.clientX;
    lastMouseY = e.clientY;
  };
  document.addEventListener("mousemove", mouseMoveHandler);

  // Main keyboard handler
  keydownHandler = (e) => {
    handleKeydown(e);
  };
  document.addEventListener("keydown", keydownHandler);
}

/**
 * Remove keyboard shortcut listeners.
 */
export function teardownKeyboardShortcuts() {
  if (mouseMoveHandler) {
    document.removeEventListener("mousemove", mouseMoveHandler);
    mouseMoveHandler = null;
  }
  if (keydownHandler) {
    document.removeEventListener("keydown", keydownHandler);
    keydownHandler = null;
  }
  callbacks = null;
}

/**
 * Temporarily disable keyboard shortcuts.
 * Used by views that need full keyboard control (e.g., markdown editor).
 */
export function disableKeyboardShortcuts() {
  shortcutsEnabled = false;
}

/**
 * Re-enable keyboard shortcuts.
 */
export function enableKeyboardShortcuts() {
  shortcutsEnabled = true;
}

/**
 * Main keydown handler.
 */
function handleKeydown(e) {
  if (!callbacks) return;
  // Another interface is shown: its own keys only.
  if (callbacks.isActive && !callbacks.isActive()) return;
  // Help is available from every view, including Markdown, without consuming
  // question marks typed into editors. Modal focus traps own all other keys.
  if (callbacks.isModalOpen()) return;
  if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey && !e.defaultPrevented && !e.isComposing && !e.repeat
      && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName) && !e.target.isContentEditable
      && !document.querySelector('.deletion-modal-overlay, .tag-popup')) {
    e.preventDefault();
    callbacks.showShortcutsModal();
    return;
  }
  if (['r', 'w'].includes(e.key.toLowerCase()) &&
      (e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented || e.isComposing || e.repeat)) return;

  // Panel toggles remain available in every view and over the panels themselves.
  // A typing surface, modal, browser shortcut or consumed event keeps its keys.
  const panelKey = e.key.toLowerCase();
  if (['n', 'r'].includes(panelKey) && !e.ctrlKey && !e.metaKey && !e.altKey && !(panelKey === 'r' && e.shiftKey)) {
    const target = e.target;
    if (!e.defaultPrevented && !e.isComposing && !e.repeat &&
        !['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) && !target.isContentEditable &&
        !document.querySelector('.deletion-modal-overlay, .tag-popup') &&
        callbacks.getState()?.data) {
      if (panelKey === 'r') {
        e.preventDefault();
        callbacks.toggleRoutinesPanel();
      } else if (callbacks.getState().currentView !== 'notes') {
        e.preventDefault();
        callbacks.toggleNotesOverlay();
      }
    }
    return;
  }

  // Keep map-editing shortcuts out of the Notes panel, without reserving the
  // entire right side of the window (including tabs/header above the panel).
  // The panel's TipTap editor handles text itself; the old notes-textarea no
  // longer exists, so redirecting keys to it silently swallowed shortcuts.
  const notesOverlay = document.getElementById('notes-overlay');
  if (notesOverlay?.classList.contains('active') &&
      notesOverlay.contains(document.elementFromPoint(lastMouseX, lastMouseY))) return;

  // Ctrl/Cmd + Z  → tree undo
  // Ctrl/Cmd + Shift + Z  → tree redo
  // Ctrl/Cmd + Y  → tree redo (Windows convention)
  //
  // Focus-follows-mouse: if the pointer is over a text-editing surface
  // (tiptap editor or plain textarea/input), let that surface handle the
  // shortcut — tiptap uses y-prosemirror's own yUndoPlugin for text undo
  // with word-level granularity. Tree undo is for structural edits only.
  if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z' || e.key === 'y' || e.key === 'Y')) {
    const isRedo = (e.key === 'y' || e.key === 'Y') || ((e.key === 'z' || e.key === 'Z') && e.shiftKey);
    const isUndo = (e.key === 'z' || e.key === 'Z') && !e.shiftKey;
    if (!isRedo && !isUndo) return;

    // If focus is in an input/textarea/contentEditable, the browser (or the
    // editor plugin) handles it — do nothing here.
    const target = e.target;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    // Focus-follows-mouse: inspect what's under the cursor.
    const hits = document.elementsFromPoint(lastMouseX, lastMouseY);
    const overEditor = hits.some(el =>
      el.classList?.contains('ProseMirror') ||
      el.tagName === 'TEXTAREA' ||
      el.tagName === 'INPUT' ||
      el.isContentEditable
    );
    if (overEditor) return;

    // Dispatch to tree UndoManager. If the stack is empty, tell the user
    // so they don't bang on the keys wondering if the app is wedged.
    e.preventDefault();
    if (isUndo) {
      if (!undoManager.canUndo()) {
        showToast(t("app.keys.nothingToUndo"), { type: "info", duration: 1500 });
      } else {
        undoManager.undo();
      }
    } else {
      if (!undoManager.canRedo()) {
        showToast(t("app.keys.nothingToRedo"), { type: "info", duration: 1500 });
      } else {
        undoManager.redo();
      }
    }
    return;
  }

  // Skip if shortcuts are temporarily disabled (e.g., markdown view active)
  if (!shortcutsEnabled) return;

  // Skip ALL global shortcuts if ANY modal is active (modals handle their own keys)
  // Also check for deletion modal which isn't in the modal system
  if (
    callbacks.isModalOpen() ||
    document.querySelector(".deletion-modal-overlay") ||
    document.querySelector(".tag-popup")
  ) {
    return;
  }

  // Typing surfaces own their keys. Without this, letters typed into the
  // inline rename editor (or the search box, or any input) fall through to
  // the single-key shortcuts below — 'a'/Tab added a node per keystroke,
  // 'c' collapsed, and Delete removed the node being renamed. The ctrl/cmd
  // sections above keep their own more nuanced guards.
  {
    const target = e.target;
    if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
      return;
    }
  }

  // Ctrl/Cmd + F to focus search
  if ((e.ctrlKey || e.metaKey) && e.key === "f") {
    e.preventDefault();
    document.getElementById("sidebar")?.classList.remove("collapsed");
    const searchInput = document.getElementById("search-input");
    searchInput?.focus();
    searchInput?.select();
    return;
  }

  // Ctrl/Cmd + C to copy node(s)
  if ((e.ctrlKey || e.metaKey) && e.key === "c") {
    // Allow normal copy when editing text
    if (
      e.target.tagName === "INPUT" ||
      e.target.tagName === "TEXTAREA" ||
      e.target.isContentEditable
    ) {
      return;
    }
    const state = callbacks.getState();
    if (!state || !state.data) return;
    const targetId = getNodeUnderCursor(state.currentView) || state.selectedId;
    if (targetId) {
      e.preventDefault();
      callbacks.copyNodes(targetId);
    }
    return;
  }

  // Ctrl/Cmd + X to cut node(s)
  if ((e.ctrlKey || e.metaKey) && e.key === "x") {
    if (
      e.target.tagName === "INPUT" ||
      e.target.tagName === "TEXTAREA" ||
      e.target.isContentEditable
    ) {
      return;
    }
    const state = callbacks.getState();
    if (!state || !state.data) return;
    // A full map allows cutting: cut and paste into another map moves a
    // branch out of it.
    if (callbacks.isDeleteBlocked()) {
      callbacks.showToast(t("app.keys.cutReadOnly"), { type: "warning" });
      return;
    }
    const targetId = getNodeUnderCursor(state.currentView) || state.selectedId;
    const rootId = state.data?.id;
    if (targetId && targetId !== rootId && targetId !== state.focusId) {
      e.preventDefault();
      callbacks.cutNodes(targetId);
    }
    return;
  }

  // Ctrl/Cmd + V to paste node(s)
  if ((e.ctrlKey || e.metaKey) && e.key === "v") {
    if (
      e.target.tagName === "INPUT" ||
      e.target.tagName === "TEXTAREA" ||
      e.target.isContentEditable
    ) {
      return;
    }
    const state = callbacks.getState();
    if (!state || !state.data) return;
    if (callbacks.isReadOnly()) {
      callbacks.showToast(t("app.keys.pasteReadOnly"), {
        type: "warning",
      });
      return;
    }
    const targetId =
      getNodeUnderCursor(state.currentView) ||
      state.selectedId ||
      state.focusId;
    if (targetId) {
      e.preventDefault();
      callbacks.pasteNodes(targetId);
    }
    return;
  }

  // Don't handle shortcuts if typing in an input or contentEditable
  if (
    e.target.tagName === "INPUT" ||
    e.target.tagName === "TEXTAREA" ||
    e.target.tagName === "SELECT" ||
    e.target.isContentEditable
  ) {
    return;
  }

  const state = callbacks.getState();
  if (!state || !state.data) return; // No data loaded yet

  const currentView = state.currentView;
  const isViewer = callbacks.isReadOnly();

  // rootId is dynamic, not always "root" (e.g., shared docs)
  const rootId = state.data?.id;

  // Escape to go up one level (not while a node is dragged: there Esc
  // cancels the drag)
  if (e.key === "Escape") {
    if (document.body.classList.contains("dragging-node")) return;
    if (state.focusId !== rootId) {
      const parent = callbacks.findParent(state.focusId);
      if (parent) {
        callbacks.setFocus(parent.id);
        callbacks.switchView(currentView);
      }
    }
    return;
  }

  // 'U' to go up one level (climb breadcrumb)
  if (e.code === "KeyU" && !e.ctrlKey && !e.metaKey) {
    e.preventDefault();
    if (state.focusId !== rootId) {
      const parent = callbacks.findParent(state.focusId);
      if (parent) {
        callbacks.setFocus(parent.id);
        callbacks.switchView(currentView);
      }
    }
    return;
  }

  // '[' to toggle sidebar
  if (e.key === "[") {
    document.getElementById("sidebar")?.classList.toggle("collapsed");
    return;
  }

  // 'T' to open tag popup for the node under the mouse (bubble, mindmap,
  // gantt: a timeline bar or a task list row)
  if (e.key === "t" || e.key === "T") {
    if (["bubble", "mindmap", "gantt"].includes(currentView) && !callbacks.isReadOnly()) {
      e.preventDefault();
      const nodeId = getNodeUnderCursor(currentView, { listRows: true });
      if (nodeId && callbacks.openTagPopup) {
        callbacks.openTagPopup(nodeId, lastMouseX, lastMouseY);
      }
    }
    return;
  }

  // W toggles the current note's execution list; Shift+R adds to a routine.
  // Browser shortcuts (Ctrl/Cmd+W/R) keep their native meaning.
  const addToRoutine = e.key.toLowerCase() === 'r' && e.shiftKey;
  const toggleExecution = e.key.toLowerCase() === 'w' && !e.shiftKey;
  if ((addToRoutine || toggleExecution) && !e.ctrlKey && !e.metaKey && !e.altKey) {
    if (e.defaultPrevented || e.isComposing || e.repeat) return;
    if (!isViewer && ["bubble", "mindmap", "gantt", "kanban"].includes(currentView)) {
      e.preventDefault();
      const hoveredId = getNodeUnderCursor(currentView, { listRows: true });
      const nodeId = hoveredId || state.selectedId;
      if (nodeId && addToRoutine && callbacks.captureRoutineNode) {
        callbacks.captureRoutineNode(nodeId);
      } else if (nodeId && toggleExecution && callbacks.toggleNodeRefnote) {
        callbacks.toggleNodeRefnote(nodeId);
      }
    }
    return;
  }

  // Block all edit shortcuts for read-only viewers
  // Allow: navigation (Escape, U), zoom (+/-), sidebar ([), notes (N)
  // Block: E (edit), C (collapse), W (execution list), Enter/F2 (rename), A/Tab (add), Delete (remove)
  if (isViewer) {
    const editKeys = [
      "e",
      "c",
      "w",
      "a",
      "enter",
      "f2",
      "delete",
      "backspace",
      "tab",
    ];
    const key = e.key.toLowerCase();
    // A full map allows deleting, to make room.
    const deleting = key === "delete" || key === "backspace";
    if (editKeys.includes(key) && !(deleting && !callbacks.isDeleteBlocked())) {
      e.preventDefault();
      callbacks.showToast(t(callbacks.isMapFull() ? "app.keys.mapFull" : "app.keys.viewOnly"), {
        type: "warning",
      });
      return;
    }
  }

  // 'E' to open properties modal for hovered/selected node
  if (
    (e.key === "e" || e.key === "E") &&
    !e.ctrlKey &&
    !e.metaKey &&
    (currentView === "mindmap" ||
      currentView === "bubble" ||
      currentView === "gantt")
  ) {
    const hoveredId = getNodeUnderCursor(currentView);
    const targetId = hoveredId || state.selectedId;
    if (targetId) {
      e.preventDefault();
      callbacks.openModal(targetId);
    }
    return;
  }

  // 'C' to collapse/expand hovered/selected node (mindmap and bubble views)
  if (
    (e.key === "c" || e.key === "C") &&
    (currentView === "mindmap" || currentView === "bubble")
  ) {
    const hoveredId = getNodeUnderCursor(currentView);
    const targetId = hoveredId || state.selectedId;
    if (targetId) {
      const node = callbacks.findNode(targetId);
      if (node && node.children?.length) {
        callbacks.updateNode(targetId, { collapsed: !node.collapsed });
        const viewModule = callbacks.getViewModule(currentView);
        if (viewModule && viewModule.update) {
          viewModule.update(state, callbacks.getState());
        }
      }
    }
    return;
  }

  // Quick node operations in mindmap view
  if (currentView === "mindmap") {
    handleMindmapShortcuts(e, state);
    return;
  }

  // Quick node operations in bubble view
  if (currentView === "bubble") {
    handleBubbleShortcuts(e, state);
    return;
  }
}

/**
 * Handle mindmap-specific shortcuts.
 */
function handleMindmapShortcuts(e, state) {
  const viewModule = callbacks.getViewModule("mindmap");

  if (e.key === "+" || e.key === "=") {
    e.preventDefault();
    if (viewModule && viewModule.zoomTowardsMouse) {
      viewModule.zoomTowardsMouse(1.15, lastMouseX, lastMouseY);
    }
    return;
  }

  if (e.key === "-" || e.key === "_") {
    e.preventDefault();
    if (viewModule && viewModule.zoomTowardsMouse) {
      viewModule.zoomTowardsMouse(1 / 1.15, lastMouseX, lastMouseY);
    }
    return;
  }

  const hoveredId = getNodeUnderCursor("mindmap");
  const targetId = hoveredId || state.selectedId || state.focusId;

  if (
    e.key === "Enter" &&
    !e.ctrlKey &&
    !e.metaKey &&
    (hoveredId || state.selectedId)
  ) {
    e.preventDefault();
    const editId = hoveredId || state.selectedId;
    const labelEl = document.querySelector(
      `.mm-node[data-id="${CSS.escape(editId)}"] .mm-title`
    );
    if (labelEl) {
      callbacks.makeEditable(labelEl, editId);
    }
    return;
  }

  if ((e.key === "a" || e.key === "A" || e.key === "Tab") && !e.shiftKey) {
    e.preventDefault();
    const parentId = hoveredId || state.focusId;
    const parent = callbacks.findNode(parentId);
    // rootId is dynamic, not always "root" (e.g., shared docs)
    const rootId = state.data?.id;

    if (parent) {
      let newColor;
      if (parent.id === rootId) {
        const existingColors = (parent.children || []).map((c) => c.color);
        newColor = callbacks.getNewColor(existingColors, null);
      } else {
        newColor = parent.color;
      }

      callbacks.openModal(null, { parentId, color: newColor, name: t("app.node.newName") });
    }
    return;
  }

  if (e.key === "F2" && (hoveredId || state.selectedId)) {
    e.preventDefault();
    const editId = hoveredId || state.selectedId;
    const labelEl = document.querySelector(
      `.mm-node[data-id="${CSS.escape(editId)}"] .mm-title`
    );
    if (labelEl) {
      callbacks.makeEditable(labelEl, editId);
    }
    return;
  }

  const deleteTargetId = hoveredId || state.selectedId;
  // rootId is dynamic, not always "root" (e.g., shared docs)
  const rootId = state.data?.id;
  if (
    (e.key === "Delete" || e.key === "Backspace") &&
    deleteTargetId &&
    deleteTargetId !== rootId &&
    deleteTargetId !== state.focusId
  ) {
    e.preventDefault();
    callbacks.removeNode(deleteTargetId);
    callbacks.setSelected(
      callbacks.findParent(deleteTargetId)?.id || state.focusId
    );
    if (viewModule && viewModule.update) {
      viewModule.update(state, callbacks.getState());
    }
    return;
  }
}

/**
 * Edit a bubble's name in place: a leaf's in its label (its own, not one of
 * a bubble inside it); a parent's, whose title is drawn on a canvas
 * (interfaces/classic/views/bubble/titles.js), in a box over that title.
 */
function editBubbleName(editId, viewModule) {
  const leafLabel = document.querySelector(
    `.bubble[data-id="${CSS.escape(editId)}"] > .bubble-label-centered`
  );
  if (leafLabel) {
    callbacks.makeEditable(leafLabel, editId, "bubble");
    return;
  }
  const node = callbacks.findNode(editId);
  const at = viewModule?.titleEditPoint?.(editId);
  if (!node || !at) return;
  const name = document.createElement("span");
  name.textContent = node.name || node.text || "";
  callbacks.makeEditable(name, editId, "bubble", at.x, at.y);
}

/**
 * Handle bubble-specific shortcuts.
 */
function handleBubbleShortcuts(e, state) {
  const viewModule = callbacks.getViewModule("bubble");
  const hoveredId = getNodeUnderCursor("bubble");
  const targetId = hoveredId || state.selectedId || state.focusId;

  if (
    e.key === "Enter" &&
    !e.ctrlKey &&
    !e.metaKey &&
    (hoveredId || state.selectedId)
  ) {
    e.preventDefault();
    editBubbleName(hoveredId || state.selectedId, viewModule);
    return;
  }

  if ((e.key === "a" || e.key === "A" || e.key === "Tab") && !e.shiftKey) {
    e.preventDefault();
    const parentId = hoveredId || state.focusId;
    const parent = callbacks.findNode(parentId);
    // rootId is dynamic, not always "root" (e.g., shared docs)
    const rootId = state.data?.id;

    if (parent) {
      let newColor;
      if (parent.id === rootId) {
        const existingColors = (parent.children || []).map((c) => c.color);
        newColor = callbacks.getNewColor(existingColors, null);
      } else {
        newColor = parent.color;
      }


      // Convert mouse position to world coordinates for bubble view
      const worldPos = callbacks.screenToWorld(
        lastMouseX,
        lastMouseY,
        parentId
      );

      const docId = callbacks.getCurrentDocId();
      callbacks.openModal(null, {
        parentId, color: newColor, name: t("app.node.newName"),
        onCreated: (node, chosenParentId) => {
          if (docId && chosenParentId === parentId) {
            callbacks.setBubblePosition(docId, node.id, worldPos.x, worldPos.y, undefined, parentId);
          }
        },
      });
    }
    return;
  }

  if (e.key === "F2" && (hoveredId || state.selectedId)) {
    e.preventDefault();
    editBubbleName(hoveredId || state.selectedId, viewModule);
    return;
  }

  const deleteTargetId = hoveredId || state.selectedId;
  // rootId is dynamic, not always "root" (e.g., shared docs)
  const rootId = state.data?.id;
  if (
    (e.key === "Delete" || e.key === "Backspace") &&
    deleteTargetId &&
    deleteTargetId !== rootId &&
    deleteTargetId !== state.focusId
  ) {
    e.preventDefault();
    callbacks.removeNode(deleteTargetId);
    callbacks.setSelected(
      callbacks.findParent(deleteTargetId)?.id || state.focusId
    );
    if (viewModule && viewModule.update) {
      viewModule.update(state, callbacks.getState());
    }
    return;
  }
}

/**
 * Get current mouse position (for external use).
 */
export function getMousePosition() {
  return { x: lastMouseX, y: lastMouseY };
}
